import axios from "axios";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "../../utils/httpCommon";
import { useApiUrls } from "../useApiUrl";
import { toast } from "../../utils/snackbarHelper";
import { toastMessage } from "../../constants/strings";
import { PlanType } from "../../types/planning/plan.type";
import {
  errorDetailSchema,
  RouteTrapStatusConstraint,
  ShipmentsNotReadyToLoad,
  transitionErrorCodesSchema,
} from "../../types/inbound/errorDetail.type";
import { safeParsePartial } from "../../utils/zodSafeParse";
import { useIsFeatureFlagEnabled } from "../../featureFlags/useIsFeatureFlagEnabled";
import { plannedRoutesQueryKey } from "./usePlannedRoutes";
import { shipmentsByRouteOrTrapKeys } from "./preplanning/useShipmentsByRouteOrTrap";

// TODO Remove all variables and types marked as "Old" when the BE has a centralized return type:
// https://dylt.atlassian.net/browse/IODD-2892
// Unfortunately until then we need to have this as a fallback for one of the calls
const deliveryRouteTrapStatusSchemaOld = z.object({
  id: z.number(),
  name: z.string(),
  isAvailableForTransition: z.boolean(),
  transitionConstraints: z.array(transitionErrorCodesSchema),
});

const deliveryRouteTrapStatusSchema = z.object({
  id: z.number(),
  name: z.string(),
  isAvailableForTransition: z.boolean(),
  transitionConstraints: z.array(transitionErrorCodesSchema),
  errorDetails: z
    .array(errorDetailSchema)
    .nullable()
    .transform((array) => array ?? []),
});

const bulkDeliveryRouteTrapStatusSchema = z.object({
  id: z.number(),
  statusOptions: z.array(deliveryRouteTrapStatusSchema),
  name: z.string(),
});

const bulkUpdateDeliveryRouteTrapStatusSchema = z.object({
  deliveryRoute: z.array(bulkDeliveryRouteTrapStatusSchema),
  deliveryTrap: z.array(bulkDeliveryRouteTrapStatusSchema),
});

const deliveryRoutesTrapsStatusSchema = z.array(deliveryRouteTrapStatusSchema);

const bulkUpdateDeliveryRouteTrapStatusResponseSchema = z.object({
  successfullyUpdatedIds: z.object({
    deliveryRoute: z.array(z.number()),
    deliveryTrap: z.array(z.number()),
  }),
  failedToUpdateIds: z.object({
    deliveryRoute: z.array(z.number()),
    deliveryTrap: z.array(z.number()),
  }),
});

export type BulkUpdateDeliveryRouteTrapStatusResponse = z.infer<
  typeof bulkUpdateDeliveryRouteTrapStatusResponseSchema
>;

export type DeliveryRouteTrapStatusOld = z.infer<
  typeof deliveryRouteTrapStatusSchemaOld
>;

export type DeliveryRouteTrapStatusResponse = z.infer<
  typeof bulkUpdateDeliveryRouteTrapStatusSchema
>;

export type DeliveryRouteTrapStatus = z.infer<
  typeof deliveryRouteTrapStatusSchema
>;

export type DeliveryRoutesTrapsBulkStatus = z.infer<
  typeof bulkDeliveryRouteTrapStatusSchema
>;

type DeliveryRouteTrapStatusTransitionDetails = z.infer<
  typeof deliveryRouteTrapStatusSchema
>;

type BulkStatusTransitionMap = {
  [name: string]: {
    status: DeliveryRoutesTrapsBulkStatus;
    isAllAvailableForTransition: boolean;
    isNoneAvailableForTransition: boolean;
    errorDetails: {
      [subject: string]: {
        [message: string]: {
          code: RouteTrapStatusConstraint;
          routeOrTrap: string;
          shipmentsData?: ShipmentsNotReadyToLoad | null;
        }[];
      };
    };
  };
};

const keys = {
  allDeliveryRouteTrapStatuses: ["delivery-route-trap-status"],
  deliveryRouteTrapStatus: (type: PlanType, id: number) => [
    "delivery-route-trap-status",
    type,
    id,
  ],
  deliveryRouteTrapStatusOld: (type: PlanType, id: number) => [
    "delivery-route-trap-status-old",
    type,
    id,
  ],
  deliveryRoutesTrapsStatus: (routeIds: number[], trapIds: number[]) => [
    "delivery-routes-traps-status",
    routeIds,
    trapIds,
  ],
  deliveryRoutesTrapsStatusOld: (routeIds: number[], trapIds: number[]) => [
    "delivery-routes-traps-status-old",
    routeIds,
    trapIds,
  ],
};

export const useDeliveryRoutesTrapsStatusOld = (
  routeIds: number[],
  trapIds: number[]
) => {
  const hookUrls = useApiUrls();
  const { getDeliveryRoutesTrapsStatusOld } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useQuery({
    enabled: routeIds.length + trapIds.length > 0,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: keys.deliveryRoutesTrapsStatusOld(routeIds, trapIds),
    queryFn: async () => {
      const { data } = await http.get(
        getDeliveryRoutesTrapsStatusOld(routeIds, trapIds)
      );
      return isSafeParseEnabled
        ? safeParsePartial(
            deliveryRoutesTrapsStatusSchema,
            data,
            "useDeliveryRoutesTrapsStatusOld"
          )
        : deliveryRoutesTrapsStatusSchema.parse(data);
    },
  });
};

type DeliveryRoutesTrapsStatusChange = {
  sicId: number;
  date: number;
  searchTerm: string;
  routeIds: number[];
  trapIds: number[];
  onSuccess?: (newStatus: DeliveryRouteTrapStatus) => void;
};

export const useDeliveryRoutesTrapsStatusChangeOld = ({
  sicId,
  date,
  routeIds,
  trapIds,
  onSuccess,
}: DeliveryRoutesTrapsStatusChange) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const { putDeliveryRoutesTrapsStatus } = hookUrls;

  return useMutation({
    mutationFn: async (newStatus: DeliveryRouteTrapStatus) => {
      await http.put(putDeliveryRoutesTrapsStatus, {
        statusId: newStatus.id,
        routeIds,
        trapIds,
      });
      return newStatus;
    },
    onSuccess: (newStatus: DeliveryRouteTrapStatus) => {
      onSuccess?.(newStatus);
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        return toast(error.response.data, {
          variant: "warning",
        });
      }

      toast(toastMessage.generics.error, {
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: keys.allDeliveryRouteTrapStatuses,
      });
      queryClient.invalidateQueries({
        queryKey: plannedRoutesQueryKey(date, sicId),
      });
    },
  });
};

const processBulkUpdateStatus = (
  data: DeliveryRouteTrapStatusResponse
): BulkStatusTransitionMap => {
  const result: BulkStatusTransitionMap = {};

  const predefinedOrder = ["CPLN", "TPLN", "CLDK", "CLDV", "TRAP", "DISP"];

  // Helper function to process status options
  const processStatusOptions = (
    routeOrTrap: string,
    statusOptions: DeliveryRouteTrapStatusTransitionDetails[]
  ) => {
    statusOptions.forEach((statusOption) => {
      const { name, isAvailableForTransition, errorDetails } = statusOption;

      // Initialize the map entry if it doesn't exist
      if (!Object.prototype.hasOwnProperty.call(result, name)) {
        result[name] = {
          status: {
            id: statusOption.id,
            name,
            statusOptions: [statusOption],
          },
          isNoneAvailableForTransition: true,
          isAllAvailableForTransition: true,
          errorDetails: {},
        };
      }

      // Set to false once ensure it will stay false
      if (!isAvailableForTransition) {
        result[name].isAllAvailableForTransition = false;
      }

      // Set to false once ensure it will stay false
      if (isAvailableForTransition) {
        result[name].isNoneAvailableForTransition = false;
      }

      // Process errorDetails and group by subject
      errorDetails.forEach(({ subject, message, data, code }) => {
        // The conditions bellow are necessary to ensure that the structure is initialized
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!result[name].errorDetails[subject]) {
          result[name].errorDetails[subject] = {};
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!result[name].errorDetails[subject][message]) {
          result[name].errorDetails[subject][message] = [];
        }
        result[name].errorDetails[subject][message].push({
          code,
          routeOrTrap,
          shipmentsData: data,
        });
      });
    });
  };

  // Process deliveryRoutes
  data.deliveryRoute.forEach((route) => {
    processStatusOptions("Route " + route.name, route.statusOptions);
  });

  // Process deliveryTraps
  data.deliveryTrap.forEach((trap) => {
    processStatusOptions("Trap " + trap.name, trap.statusOptions);
  });

  // Sort the result based on the predefined order
  const sortedResult: BulkStatusTransitionMap = {};
  predefinedOrder.forEach((key) => {
    for (const name in result) {
      if (name.startsWith(key)) {
        sortedResult[name] = result[name];
      }
    }
  });

  // Add any remaining keys that were not in the predefined order
  for (const name in result) {
    sortedResult[name] = result[name];
  }

  return sortedResult;
};

export const useDeliveryRoutesTrapsStatusV3 = (
  routeIds: number[],
  trapIds: number[]
) => {
  const hookUrls = useApiUrls();
  const { getDeliveryRoutesTrapsStatusV3 } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useQuery({
    enabled: routeIds.length + trapIds.length > 0,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: keys.deliveryRoutesTrapsStatus(routeIds, trapIds),
    queryFn: async (): Promise<BulkStatusTransitionMap> => {
      const { data } = await http.get(
        getDeliveryRoutesTrapsStatusV3(routeIds, trapIds)
      );
      const parsedResult = isSafeParseEnabled
        ? safeParsePartial(
            bulkUpdateDeliveryRouteTrapStatusSchema,
            data,
            "useDeliveryRoutesTrapsStatusV3"
          )
        : bulkUpdateDeliveryRouteTrapStatusSchema.parse(data);

      return processBulkUpdateStatus(parsedResult);
    },
  });
};

export const useDeliveryRouteTrapStatusV3 = (
  type: PlanType,
  id: number,
  enabled: boolean
) => {
  const hookUrls = useApiUrls();
  const { getDeliveryRoutesTrapsStatusV3 } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useQuery({
    enabled,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: keys.deliveryRouteTrapStatus(type, id),
    queryFn: async () => {
      const { data } = await http.get(
        getDeliveryRoutesTrapsStatusV3(
          type === "delivery_route" ? [id] : [],
          type === "delivery_trap" ? [id] : []
        )
      );
      const parsedResult = isSafeParseEnabled
        ? safeParsePartial(
            bulkUpdateDeliveryRouteTrapStatusSchema,
            data,
            "useDeliveryRouteTrapStatusV3"
          )
        : bulkUpdateDeliveryRouteTrapStatusSchema.parse(data);
      return type === "delivery_route"
        ? parsedResult.deliveryRoute[0].statusOptions
        : parsedResult.deliveryTrap[0].statusOptions;
    },
  });
};

type DeliveryRoutesTrapsStatusChangeV2 = {
  sicId: number;
  date: number;
  routeIds: number[];
  trapIds: number[];
  onSuccess?: (
    updateResponse: BulkUpdateDeliveryRouteTrapStatusResponse,
    newStatus: DeliveryRoutesTrapsBulkStatus
  ) => void;
};

export const useDeliveryRoutesTrapsStatusChangeV2 = ({
  sicId,
  date,
  routeIds,
  trapIds,
  onSuccess,
}: DeliveryRoutesTrapsStatusChangeV2) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const { putDeliveryRoutesTrapsStatusV2 } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useMutation({
    mutationFn: async (
      newStatus: DeliveryRoutesTrapsBulkStatus
    ): Promise<BulkUpdateDeliveryRouteTrapStatusResponse> => {
      const url = putDeliveryRoutesTrapsStatusV2;
      const { data } = await http.put(url, {
        statusId: newStatus.id,
        routeIds,
        trapIds,
      });
      return isSafeParseEnabled
        ? safeParsePartial(
            bulkUpdateDeliveryRouteTrapStatusResponseSchema,
            data,
            "useDeliveryRoutesTrapsStatusChangeV2"
          )
        : bulkUpdateDeliveryRouteTrapStatusResponseSchema.parse(data);
    },
    onSuccess: (
      updateResponse: BulkUpdateDeliveryRouteTrapStatusResponse,
      newStatus: DeliveryRoutesTrapsBulkStatus
    ) => {
      onSuccess?.(updateResponse, newStatus);
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        return toast(error.response.data, {
          variant: "warning",
        });
      }

      toast(toastMessage.generics.error, {
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: keys.allDeliveryRouteTrapStatuses,
      });
      queryClient.invalidateQueries({
        queryKey: plannedRoutesQueryKey(date, sicId),
      });
    },
  });
};

type DeliveryRouteTrapStatusChangeV2 = {
  sicId: number;
  date: number;
  type: PlanType;
  routeOrTrapId: number;
  onSuccess?: (
    updateResponse: BulkUpdateDeliveryRouteTrapStatusResponse,
    newStatus: DeliveryRouteTrapStatus
  ) => void;
};

export const useDeliveryRouteTrapStatusChangeV2 = ({
  sicId,
  date,
  type,
  routeOrTrapId,
  onSuccess,
}: DeliveryRouteTrapStatusChangeV2) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const { putDeliveryRoutesTrapsStatusV2 } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  return useMutation({
    mutationFn: async (
      newStatus: DeliveryRouteTrapStatus
    ): Promise<BulkUpdateDeliveryRouteTrapStatusResponse> => {
      const { data } = await http.put(putDeliveryRoutesTrapsStatusV2, {
        statusId: newStatus.id,
        routeIds: type === "delivery_route" ? [routeOrTrapId] : undefined,
        trapIds: type === "delivery_trap" ? [routeOrTrapId] : undefined,
      });
      return isSafeParseEnabled
        ? safeParsePartial(
            bulkUpdateDeliveryRouteTrapStatusResponseSchema,
            data,
            "useDeliveryRouteTrapStatusChangeV2"
          )
        : bulkUpdateDeliveryRouteTrapStatusResponseSchema.parse(data);
    },
    onSuccess: (
      updateResponse: BulkUpdateDeliveryRouteTrapStatusResponse,
      newStatus: DeliveryRouteTrapStatus
    ) => {
      onSuccess?.(updateResponse, newStatus);
    },
    onError: (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        return toast(error.response.data, {
          variant: "warning",
        });
      }

      toast(toastMessage.generics.error, {
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: keys.allDeliveryRouteTrapStatuses,
      });
      queryClient.invalidateQueries({
        queryKey: plannedRoutesQueryKey(date, sicId),
      });
      queryClient.invalidateQueries({
        queryKey: shipmentsByRouteOrTrapKeys.shipmentsByRouteOrTrap(
          routeOrTrapId,
          type
        ),
      });
    },
  });
};
